import * as React from "react";

import { cn } from "@/shared/utils/utils";
import type { CustomChartSkeletonProps } from "@/shared/type/skeleton.type";
import { Skeleton } from "./skeleton";

export const CustomChartSkeleton = ({
  barCount = 10,
  className,
  contentClassName,
  chartType = "bar",
  ...props
}: CustomChartSkeletonProps): React.ReactElement => {
  const barIndexes = [...Array(Math.max(1, barCount)).keys()];

  if (chartType === "line") {
    const points = barIndexes.map((barIndex) => ({
      x: 6 + (barIndex * 88) / Math.max(1, barCount - 1),
      y: 72 - ((barIndex * 15 + 10) % 54),
    }));
    const linePath = points
      .map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`)
      .join(" ");

    return (
      <div
        aria-label="Loading dashboard chart"
        className={cn("white-card h-[337px]", className)}
        {...props}
      >
        <Skeleton className="mb-5 h-5 w-44 max-w-1/2" />
        <div
          className={cn(
            "relative h-[250px] overflow-hidden border-b border-l border-border bg-muted/10 px-4 pb-3",
            contentClassName,
          )}
        >
          <div className="pointer-events-none absolute inset-x-0 top-1/4 border-t border-border/60" />
          <div className="pointer-events-none absolute inset-x-0 top-2/4 border-t border-border/60" />
          <div className="pointer-events-none absolute inset-x-0 top-3/4 border-t border-border/60" />
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="relative h-full w-full"
            aria-hidden="true"
          >
            <path
              d={linePath}
              fill="none"
              stroke="var(--muted-foreground)"
              strokeOpacity="0.24"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {points.map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r="1"
                fill="var(--muted-foreground)"
                fillOpacity="0.24"
              />
            ))}
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div
      aria-label="Loading dashboard chart"
      className={cn("white-card h-[337px]", className)}
      {...props}
    >
      <Skeleton className="mb-5 h-5 w-44 max-w-1/2" />
      <div
        className={cn(
          "flex h-[250px] items-end gap-3 border-b border-l border-border px-4 pb-3",
          contentClassName,
        )}
      >
        {barIndexes.map((barIndex) => (
          <Skeleton
            key={barIndex}
            className="min-w-3 flex-1"
            style={{ height: `${35 + ((barIndex * 17) % 55)}%` }}
          />
        ))}
      </div>
    </div>
  );
};
